import React, { Suspense } from 'react'
import {
	Route,
	Routes
} from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getCoreRoutes } from '../shared/coreRoutes'
import { getAuthRoutes } from '../shared/authRoutes'
import { getCommonRoutes } from '../shared/commonRoutes'
import SmartRedirect from "components/SmartRedirect/SmartRedirect"
import Dashboard from '../../containers/Home/Dashboard/Dashboard'
import { getIsManager } from '../../redux/selectors'
import RouteChunkFallback from '../shared/RouteChunkFallback'
import { SandboxAccessGate } from 'components/SandboxAccess'
import { SANDBOX_FEATURE } from 'helpers/sandboxAccess'
import {
	LazyAddons,
	LazyAddNewsItem,
	LazyAddStore,
	LazyAddTransactions,
	LazyCheckout,
	LazyDueDates,
	LazyNews,
	LazyNewsItem,
	LazyOrderRegistration,
	LazyProductsPageMain,
	LazyProductFormPage,
	LazyCreatePpwrLabelPage,
	LazyCreateGpsrLabelPage,
	LazyPackagingDeclarationOfConformityPage,
	LazyPpwrLabelPreviewComparisonPage,
	LazyPpwrDigitalLabelWizardPage,
	LazyReports,
	LazyStartGuidePage,
	LazyStores,
	LazySubscription,
	LazyTaxSettings,
	LazyThreshold,
	LazyTransactions,
} from '../shared/heavyRouteChunks'

const RoutesForAuthorize = ({
	isLogged,
	isLoggingIn
}) => {
	const state = useSelector((state) => state)
	const openProjectName = state.user.openProjectName
	const IS_MANAGER = getIsManager(state)

	// Use shared routes instead of duplicating
	const commonRoutes = getCommonRoutes()
	const notLoggedInRoutes = getAuthRoutes(isLoggingIn)

	const loggedInRoutes = (
			<>
				{getCoreRoutes()}
				<Route
					path='/start-guide'
					element={<LazyStartGuidePage />}
				/>
				<Route
						path='/home'
						element={<Dashboard />}
				/>
				<Route
						path='/'
						element={<Dashboard />}
				/>
				<Route
						path='/products'
						element={<LazyProductsPageMain />}
				/>
				<Route
						path='/products/new'
						element={<LazyProductFormPage />}
				/>
				<Route
						path='/products/:productId/edit'
						element={<LazyProductFormPage />}
				/>
				<Route
						path='/packaging'
						element={<LazyProductsPageMain />}
				/>
				<Route
						path='/packaging/new/:tab?'
						element={<LazyProductFormPage />}
				/>
				<Route
						path='/packaging/:productId/edit/:tab?'
						element={<LazyProductFormPage />}
				/>
				{/* Same page element as above: switching between these keeps one ProductFormPage instance. */}
				<Route
						path='/packaging/:productId/edit/conformity/:declarationId'
						element={<LazyProductFormPage />}
				/>
				<Route
						path='/packaging/:productId/conformity/create'
						element={<LazyPackagingDeclarationOfConformityPage />}
				/>
				<Route
						path='/products/ppwr-label/create'
						element={<LazyCreatePpwrLabelPage />}
				/>
				<Route
						path='/products/gpsr-label/create'
						element={<LazyCreateGpsrLabelPage />}
				/>
				<Route
						path='/products/ppwr-label/preview-concepts'
						element={<LazyPpwrLabelPreviewComparisonPage />}
				/>
				<Route
						path='/products/ppwr-label/v2/create'
						element={<LazyPpwrDigitalLabelWizardPage />}
				/>
				<Route
						path='/home/news'
						element={<LazyNews />}
				/>
				<Route
						path='/home/news/:newsId'
						element={<LazyNewsItem />}
				/>
				{IS_MANAGER && (<Route
						path='/home/add-news'
						element={<LazyAddNewsItem />}
				/>)}
				<Route
						path='/home/due-dates'
						element={<LazyDueDates />}
				/>
				<Route
						path='/home/threshold'
						element={<LazyThreshold />}
				/>

				<Route
						path='/transactions'
						element={<LazyTransactions />}
				/>
				<Route
						path='/transactions/import'
						element={<LazyAddTransactions />}
				/>
				<Route
						path='/stores'
						element={<LazyStores openProjectName={openProjectName} />}
				/>
				<Route
						path='/stores/add'
						element={<LazyAddStore openProjectName={openProjectName} />}
				/>
				<Route
						path='/reports'
						element={
							<SandboxAccessGate feature={SANDBOX_FEATURE.REPORTS}>
								<LazyReports />
							</SandboxAccessGate>
						}
				/>
				<Route
						path='/settings'
						element={<LazyTaxSettings/>}
				/>
				<Route
						path='/settings/order'
						element={<LazyOrderRegistration />}
				/>
				<Route
						path='/integrations'
						element={<LazyStores openProjectName={openProjectName} />}
				/>
				<Route
						path='/integrations/add'
						element={<LazyAddStore openProjectName={openProjectName} />}
				/>

				<Route
						path='/subscription'
						element={<LazySubscription />}
				/>
				<Route
						path='/subscription/checkout'
						element={
							<SandboxAccessGate feature={SANDBOX_FEATURE.PAYMENT_MUTATIONS}>
								<LazyCheckout />
							</SandboxAccessGate>
						}
				/>
				<Route
						path='/addons'
						element={
							<SandboxAccessGate feature={SANDBOX_FEATURE.PAYMENT_MUTATIONS}>
								<LazyAddons />
							</SandboxAccessGate>
						}
				/>

				{/* Payment success routes */}
				<Route
						path='/payments/subscription_bin_success/'
						element={<SmartRedirect />}
				/>
				<Route
						path='/api/payments/subscription_bin_success/'
						element={<SmartRedirect />}
				/>

				<Route
						path='*'
						element={<SmartRedirect />}
				/>
			</>
	)

	return (
			<Suspense fallback={<RouteChunkFallback />}>
				<Routes >
					{commonRoutes}
					{isLogged ? loggedInRoutes : notLoggedInRoutes}
				</Routes >
			</Suspense>
	)
}

export default RoutesForAuthorize
